import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Box, Button, Typography } from '@mui/material';

import { DataTable, type TableColumn, Loading, ErrorMessage } from '@/src/ui';
import { apiRequest } from '@/src/services';
import { ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { StudentStandardCorrectnessDTO } from '../models/classStandardsReportDTOs';
import type { StandardsReportFilterState } from '../../_shared/models/standardsReportFilterDTOs';

interface StandardBreakdownModalProps {
    open: boolean;
    onClose: () => void;
    standardCode: string;
    standardIds: number[];
    filterState: StandardsReportFilterState;
}

export default function StandardBreakdownModal({
    open,
    onClose,
    standardCode,
    standardIds,
    filterState,
}: StandardBreakdownModalProps) {
    const [data, setData] = useState<StudentStandardCorrectnessDTO[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        setError('');
        setLoading(true);
        try {
            const params = new URLSearchParams({
                districtId: String(filterState.districtId),
                schoolId: String(filterState.schoolId),
                gradeLevelMasterId: String(filterState.gradeLevelMasterId),
                classId: String(filterState.classId),
                examTypeMasterId: String(filterState.examTypeMasterId || 0),
            });
            for (const id of filterState.examIds ?? []) params.append('examIds', String(id));
            for (const id of standardIds) params.append('standardIds', String(id));
            const res = await apiRequest<StudentStandardCorrectnessDTO[]>(
                'GET',
                `reports/classStandardsReport/byStandard?${params.toString()}`
            );
            setData(Array.isArray(res.data) ? res.data : []);
        } catch {
            setError('Failed to load standard breakdown.');
        } finally {
            setLoading(false);
        }
    }, [filterState, standardIds]);

    useEffect(() => {
        if (!open) return;
        setData(null);
        fetchData();
    }, [open, fetchData]);

    const rows = useMemo(() => {
        if (!data) return [];
        return [...data]
            .sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''))
            .map((r) => {
                const total = (r.questionsCorrect ?? 0) + (r.questionsIncorrect ?? 0);
                const percent =
                    total > 0 ? Math.round(((r.questionsCorrect ?? 0) / total) * 100) : 0;
                return { ...r, total, percent };
            });
    }, [data]);

    const totals = useMemo(() => {
        const correct = rows.reduce((s, r) => s + (r.questionsCorrect ?? 0), 0);
        const incorrect = rows.reduce((s, r) => s + (r.questionsIncorrect ?? 0), 0);
        const total = correct + incorrect;
        const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
        return { correct, incorrect, total, percent };
    }, [rows]);

    const columns: TableColumn[] = [
        {
            dataKey: 'studentName',
            header: 'Student',
            allowSort: true,
            align: 'left',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'questionsCorrect',
            header: 'Correct',
            allowSort: true,
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (r: any) => `${r.questionsCorrect ?? 0}/${r.total ?? 0}`,
        },
        {
            dataKey: 'percent',
            header: 'Percent',
            allowSort: true,
            align: 'center',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (r: any) => `${r.percent ?? 0}%`,
        },
    ];

    return (
        <Modal open={open} onClose={onClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 3,
                    width: '85%',
                    maxWidth: 900,
                    maxHeight: '85vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h6" fontWeight="bold" mb={1}>
                    {standardCode} – Student Breakdown
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Class total: <strong>{totals.correct}</strong> correct of{' '}
                    <strong>{totals.total}</strong> ({totals.percent}%)
                </Typography>

                <Box sx={{ position: 'relative', flex: 1, overflow: 'auto' }}>
                    <Loading visible={loading} message="Loading breakdown..." />
                    <ErrorMessage error={error} />

                    {data && !loading && !error && (
                        <DataTable
                            data={rows}
                            columns={columns}
                            tableName="StandardBreakdown"
                            rowsPerPageInitial={50}
                            alternatingRowColor
                            defaultSortColumn="studentName"
                            defaultSortDirection="asc"
                            emptyMessage="No student data found for this standard."
                        />
                    )}
                </Box>

                <Box textAlign="right" mt={2}>
                    <Button variant="outlined" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
